import React, { useState, useEffect } from 'react';
import { HiOutlinePlay, HiOutlinePhotograph, HiOutlineHashtag, HiOutlineFilter } from 'react-icons/hi';
import './Media.css';
import { mediaService } from '../services/mediaService';
import { supabase } from '../supabaseClient';
import { normalizeStorageUrl } from '../utils/storageUrl';

function Media() {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [mediaData, setMediaData] = useState([]);
  const [mediaImages, setMediaImages] = useState([]);
  const [socialImages, setSocialImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('image'); // changed default from 'all' to 'image'
  const [searchTerm, setSearchTerm] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const FALLBACK_SVG = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNzUgMTI1SDIyNVYxNzVIMTc1VjEyNVoiIGZpbGw9IiM5QzlDOTkiLz4KPHR5ZXh0IHg9IjIwMCIgeT0iMjIwIiBmb250LWZhbWlseT0ic3lzdGVtLXVpIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOUM5Qzk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5Nw6lkaWEgbm9uIGRpc3BvbmlibGU8L3RleHQ+Cjwvc3ZnPg==';

  useEffect(() => {
    loadMediaData();
  }, []);

  useEffect(() => {
    const allSourceUrls = [
      ...mediaData.map(item => item.thumbnail),
      ...mediaImages.map(item => item.imageUrl),
      ...socialImages.map(item => item.imageUrl)
    ].filter(Boolean);

    const staleUrls = allSourceUrls.filter(url => url.includes('.supabase.co/storage/v1/object/public/'));
    if (staleUrls.length) {
      console.warn('[Media] Original image URLs before normalization:', staleUrls.slice(0, 10));
    }
  }, [mediaData, mediaImages, socialImages]);

  const loadMediaData = async () => {
    try {
      setLoading(true);
      
      // Get active edition first
      const { data: activeEdition, error: editionError } = await supabase
        .from('editions')
        .select('year')
        .eq('is_active', true)
        .single();
      
      let editionYear = '2025'; // Default fallback
      
      if (editionError) {
        // No active edition found
      } else if (activeEdition) {
        editionYear = activeEdition.year;
      }

      // Load all media data
      const [videoData, imageData, socialData] = await Promise.all([
        mediaService.getMediaData(editionYear),
        mediaService.getMediaImages(editionYear),
        mediaService.getSocialMediaImages(editionYear)
      ]);

      setMediaData(videoData || []);
      setMediaImages(imageData || []);
      setSocialImages(socialData || []);
      setError(null);
    } catch (error) {
      setError('Failed to load media data');
      setMediaData([]);
      setMediaImages([]);
      setSocialImages([]);
    } finally {
      setLoading(false);
    }
  };

  const openVideo = (video) => {
    setSelectedVideo(video);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  const openImage = (image) => {
    setSelectedImage(image);
  };

  const closeImage = () => {
    setSelectedImage(null);
  };

  // Combine all media items for the unified grid
  const getAllMediaItems = () => {
    const allItems = [];
    
    // Add videos
    mediaData.forEach(video => {
      allItems.push({
        ...video,
        type: 'video',
        mediaType: 'video',
        thumbnail: normalizeStorageUrl(video.thumbnail),
        url: video.videoUrl,
        persons: video.persons || video.people || video.personnes || [] // include persons for videos if available
      });
    });

    // Add gallery images
    mediaImages.forEach(image => {
      allItems.push({
        ...image,
        type: 'image',
        mediaType: 'gallery',
        thumbnail: normalizeStorageUrl(image.imageUrl),
        url: normalizeStorageUrl(image.imageUrl),
        persons: image.persons || image.people || image.personnes || [] // normalize people field
      });
    });

    // Add social images
    socialImages.forEach(image => {
      allItems.push({
        ...image,
        type: 'social',
        mediaType: 'social',
        thumbnail: normalizeStorageUrl(image.imageUrl),
        url: normalizeStorageUrl(image.imageUrl),
        persons: image.persons || image.people || image.personnes || []
      });
    });

    return allItems;
  };

  // Filter media items
  const getFilteredItems = () => {
    let items = getAllMediaItems();
    
    // Apply filter
    if (activeFilter !== 'all') {
      items = items.filter(item => item.type === activeFilter);
    }
    
    // Apply search
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(item => 
        (item.title && item.title.toLowerCase().includes(q)) ||
        (item.description && item.description.toLowerCase().includes(q)) ||
        // match persons array (if any)
        (Array.isArray(item.persons) && item.persons.some(p => String(p).toLowerCase().includes(q)))
      );
    }
    
    return items;
  };

  const handleItemClick = (item) => {
    if (item.type === 'video') {
      setSelectedVideo(item);
    } else {
      setSelectedImage(item);
    }
  };

  const getItemSize = (item, index) => {
    // Create varied sizes for masonry effect
    if (item.type === 'video') return 'large';
    if (index % 5 === 0) return 'large';
    if (index % 3 === 0) return 'medium';
    return 'small';
  };

  // helper to create safe filename
  const safeFilename = (name) => {
    if (!name) return 'image';
    return name.replace(/[^a-z0-9_\-\.]/gi, '_').slice(0, 80);
  };

  // load external script and wait for global to appear
  const loadScript = (src, globalName, timeout = 10000) => {
    return new Promise((resolve, reject) => {
      if (window[globalName]) return resolve(window[globalName]);
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      let settled = false;
      const t = setTimeout(() => {
        if (!settled) {
          settled = true;
          s.remove();
          reject(new Error('Script load timeout'));
        }
      }, timeout);
      s.onload = () => {
        if (!settled) {
          settled = true;
          clearTimeout(t);
          if (window[globalName]) resolve(window[globalName]);
          else reject(new Error('Global not found after script load'));
        }
      };
      s.onerror = (e) => {
        if (!settled) {
          settled = true;
          clearTimeout(t);
          s.remove();
          reject(e || new Error('Script load error'));
        }
      };
      document.head.appendChild(s);
    });
  };

  // download current filtered images into a zip with folder named "cigsida" or by person search
  const downloadSearchResults = async () => {
    const items = getFilteredItems().filter(i => i.type !== 'video'); // images & social
    if (!items.length) {
      alert('Aucun résultat d\'image à télécharger');
      return;
    }

    setDownloading(true);
    setDownloadProgress(0);

    // determine folder name based on searched person (if any)
    let matchedPerson = null;
    if (searchTerm && searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      for (const it of items) {
        if (Array.isArray(it.persons)) {
          const found = it.persons.find(p => String(p).toLowerCase().includes(q));
          if (found) { matchedPerson = String(found).trim(); break; }
        }
      }
    }
    const folderName = matchedPerson ? `cigsida_${safeFilename(matchedPerson)}` : 'cigsida';

    let JSZip = null;

    try {
      // try loading from CDN directly to avoid webpack build errors
      if (!JSZip && typeof window !== 'undefined') {
        try {
          const global = await loadScript('https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js', 'JSZip', 15000);
          JSZip = global || window.JSZip;
        } catch (cdnErr) {
        }
      }

      if (!JSZip) throw new Error('JSZip not available');

      const zip = new JSZip();
      const folder = zip.folder(folderName);

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        try {
          // fetch the image as blob
          const res = await fetch(item.url, { mode: 'cors' });
          if (!res.ok) throw new Error('Failed to fetch image: ' + res.status);
          const blob = await res.blob();

          // derive extension
          let ext = 'jpg';
          if (blob.type) {
            const m = blob.type.split('/')[1];
            if (m) ext = m.split('+')[0];
          } else if (item.url) {
            const parts = item.url.split('?')[0].split('.');
            if (parts.length) ext = parts.pop().slice(0, 5);
          }

          // build filename: prefer matchedPerson (if present), else item title/id
          const namePrefix = matchedPerson ? matchedPerson : (item.title || item.id);
          const filename = `${String(i+1).padStart(3,'0')}_${safeFilename(namePrefix)}.${ext}`;
          folder.file(filename, blob);
        } catch (errInner) {
        }

        setDownloadProgress(Math.round(((i + 1) / items.length) * 30));
      }

      const blob = await zip.generateAsync({ type: 'blob' }, (meta) => {
        setDownloadProgress(30 + Math.round(meta.percent * 0.7));
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${folderName}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Téléchargement impossible via zip. Vous pouvez installer JSZip localement (npm i jszip) pour activer l\'export ZIP, ou télécharger les images individuellement. Les images s\'ouvriront dans de nouveaux onglets.');
      items.forEach(it => {
        try { window.open(it.url, '_blank'); } catch(e) {}
      });
    } finally {
      setDownloadProgress(0);
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="media-container">
        <div className="media-header">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  const filteredItems = getFilteredItems();
  const totalCounts = {
    all: getAllMediaItems().length,
    video: mediaData.length,
    image: mediaImages.length,
    social: socialImages.length
  };

  return (
    <div className="media-container">
      {/* Modern Header */}
      <div className="media-hero">
        <div className="hero-content">
          <h2 className="hero-title">Galerie Média</h2>
          <p className="hero-subtitle">Revivez les meilleurs moments en images et en vidéos</p>
        </div>
        {/* <div className="hero-stats">
          <div className="stat-item">
            <span className="stat-number">{totalCounts.video}</span>
            <span className="stat-label">Vidéos</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{totalCounts.image}</span>
            <span className="stat-label">Images</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{totalCounts.social}</span>
            <span className="stat-label">Réseaux Sociaux</span>
          </div>
        </div> */}
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-controls">
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${activeFilter === 'video' ? 'active' : ''}`}
              onClick={() => setActiveFilter('video')}
            >
              <HiOutlinePlay />
              Vidéos ({totalCounts.video})
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'image' ? 'active' : ''}`}
              onClick={() => setActiveFilter('image')}
            >
              <HiOutlinePhotograph />
              Images ({totalCounts.image})
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'social' ? 'active' : ''}`}
              onClick={() => setActiveFilter('social')}
            >
              <HiOutlineHashtag />
              Réseaux Sociaux ({totalCounts.social})
            </button>
          </div>
          
          <div className="search-box">
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button
              type="button"
              className="filter-btn download-btn"
              onClick={downloadSearchResults}
              disabled={downloading}
            >
              {downloading ? `Téléchargement ${downloadProgress}%` : 'Télécharger résultats'}
            </button>
          </div>
        </div>
      </div>

      {/* Media Wall */}
      <div className="media-wall">
        {filteredItems.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <HiOutlinePhotograph />
            </div>
            <p>Aucun média trouvé</p>
            <p>Essayez de modifier vos filtres ou votre recherche</p>
          </div>
        ) : (
          <div className="media-grid">
            {filteredItems.map((item, index) => (
              <div 
                key={`${item.type}-${item.id}`} 
                className={`media-item ${item.type} ${getItemSize(item, index)}`}
                onClick={() => handleItemClick(item)}
              >
                <div className="media-card">
                  <div className="media-thumbnail">
                    <img 
                      src={item.thumbnail || FALLBACK_SVG} 
                      alt={item.title}
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = FALLBACK_SVG;
                      }}
                    />
                    
                    {/* Media Type Overlay */}
                    <div className="media-overlay">
                      <div className="media-type-icon">
                        {item.type === 'video' && <HiOutlinePlay />}
                        {item.type === 'image' && <HiOutlinePhotograph />}
                        {item.type === 'social' && <HiOutlineHashtag />}
                      </div>
                      
                      {item.type === 'video' && item.duration && (
                        <div className="duration-badge">{item.duration}</div>
                      )}
                      
                      {item.type === 'social' && item.socialPlatform && (
                        <div className="platform-badge">{item.socialPlatform}</div>
                      )}
                    </div>
                  </div>
                  
                  {/* <div className="media-info">
                    <h3 className="media-title">{item.title}</h3>
                    <p className="media-description">{item.description}</p>
                  </div> */}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="media-modal" onClick={() => setSelectedVideo(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-button" onClick={() => setSelectedVideo(null)}>×</button>
            <div className="video-player">
              <iframe
                src={selectedVideo.videoUrl}
                title={selectedVideo.title}
                frameBorder="0"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              ></iframe>
            </div>
            <div className="video-details">
              <h2>{selectedVideo.title}</h2>
              <p>{selectedVideo.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="media-modal" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-button" onClick={() => setSelectedImage(null)}>×</button>
            <div className="image-display">
              <img 
                src={selectedImage.imageUrl || selectedImage.url || FALLBACK_SVG} 
                alt={selectedImage.title} 
                onError={(e) => {
                  e.target.src = FALLBACK_SVG;
                }}
              />
            </div>
            <div className="image-details">
              <h2>{selectedImage.title}</h2>
              <p>{selectedImage.description}</p>
              {selectedImage.socialPlatform && (
                <span className="platform-badge-large">{selectedImage.socialPlatform}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Media;